import { useCallback, useRef, useEffect } from 'react';

/**
 * Hook for video completion notifications
 * Supports: Browser notifications, sound alerts, and tab title changes
 */
export const useVideoNotifications = () => {
  const originalTitle = useRef(document.title);
  const audioRef = useRef(null);

  // Initialize audio on mount
  useEffect(() => {
    // Create audio context for notification sound
    audioRef.current = new Audio();
    // Use a simple base64 encoded notification sound (short chime)
    audioRef.current.src = 'data:audio/wav;base64,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';
    audioRef.current.volume = 0.5;
    
    return () => {
      // Restore original title on unmount
      document.title = originalTitle.current;
    };
  }, []);

  // Request notification permission
  const requestNotificationPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.log('Browser does not support notifications');
      return false;
    }
    
    if (Notification.permission === 'granted') {
      return true;
    }
    
    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    
    return false;
  }, []);

  // Update tab title
  const updateTabTitle = useCallback((status, videoTitle = '') => {
    switch (status) {
      case 'generating':
        document.title = `⏳ Creating Video... | iGoViral`;
        break;
      case 'complete':
        document.title = `✅ Video Ready! | iGoViral`;
        break;
      case 'failed':
        document.title = `❌ Video Failed | iGoViral`;
        break;
      default:
        document.title = originalTitle.current;
    }
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    try {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(e => console.log('Audio play failed:', e));
      }
    } catch (e) {
      console.log('Sound notification error:', e);
    }
  }, []);

  // Show browser notification
  const showBrowserNotification = useCallback((title, body, options = {}) => {
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      return;
    }
    
    try {
      const notification = new Notification(title, {
        body,
        icon: '/logo192.png', // App icon
        badge: '/logo192.png',
        tag: 'video-complete', // Prevents duplicate notifications
        requireInteraction: false,
        ...options
      });
      
      // Auto-close after 5 seconds
      setTimeout(() => notification.close(), 5000);
      
      // Focus window when notification is clicked
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    } catch (e) {
      console.log('Browser notification error:', e);
    }
  }, []);

  // Main notification function - call this when video is ready
  const notifyVideoComplete = useCallback((videoTitle = 'Your video') => {
    // 1. Update tab title
    updateTabTitle('complete');
    
    // 2. Play sound
    playNotificationSound();
    
    // 3. Show browser notification
    showBrowserNotification(
      '🎬 Video Ready!',
      `${videoTitle} has finished generating. Click to view.`
    );
    
    // Reset tab title after 10 seconds
    setTimeout(() => {
      updateTabTitle('reset');
    }, 10000);
  }, [updateTabTitle, playNotificationSound, showBrowserNotification]);

  // Notify video failed
  const notifyVideoFailed = useCallback((videoTitle = 'Your video', error = '') => {
    updateTabTitle('failed');
    
    showBrowserNotification(
      '❌ Video Failed',
      `${videoTitle} could not be generated. ${error}`
    );
    
    setTimeout(() => {
      updateTabTitle('reset');
    }, 10000);
  }, [updateTabTitle, showBrowserNotification]);

  return {
    requestNotificationPermission,
    updateTabTitle,
    playNotificationSound,
    showBrowserNotification,
    notifyVideoComplete,
    notifyVideoFailed,
    isNotificationSupported: 'Notification' in window,
    notificationPermission: typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  };
};

export default useVideoNotifications;
